import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STORAGE_KEY = "eggman_popup_dismiss";

function readDismissed() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
}
function writeDismissed(d) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); } catch {}
}

function matchPath(popup, pathname) {
  const pref = (popup.path_prefix || "/").trim();
  if (pref === "/" || pref === "*") return true;
  return pathname === pref || pathname.startsWith(pref + "/");
}

function matchAudience(popup, user) {
  const a = popup.audience || "all";
  if (a === "all") return true;
  if (a === "guest") return !user;
  if (a === "customer") return user?.role === "customer";
  if (a === "wholesale") return user?.role === "wholesale";
  return true;
}

function isDismissed(popup) {
  const map = readDismissed();
  const ts = map[popup.id];
  if (!ts) return false;
  const days = Number(popup.dismiss_days ?? 7);
  return Date.now() - ts < days * 24 * 3600 * 1000;
}

const TONE = {
  green:  { wrap: "bg-[#0E2818] text-white", accent: "text-[#F4C430]", btn: "bg-[#EA580C] hover:bg-[#c2410c] text-white" },
  orange: { wrap: "bg-[#EA580C] text-white", accent: "text-white",     btn: "bg-white hover:bg-stone-100 text-[#EA580C]" },
  dark:   { wrap: "bg-stone-900 text-white", accent: "text-[#F4C430]", btn: "bg-[#EA580C] hover:bg-[#c2410c] text-white" },
  light:  { wrap: "bg-white text-stone-900 border-2 border-stone-200", accent: "text-[#EA580C]", btn: "bg-[#166534] hover:bg-[#14532d] text-white" },
};

/**
 * Renders the first ACTIVE popup that matches the current path + audience and
 * hasn't been dismissed yet. Loads CMS once and stays mounted at the app root.
 */
export default function PopupManager({ popups }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [shown, setShown] = useState(null);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    setShown(null);
    setClosed(false);
    if (!popups || !popups.length) return;
    const candidate = popups.find(
      (p) => p.active !== false && matchPath(p, pathname) && matchAudience(p, user) && !isDismissed(p)
    );
    if (!candidate) return;
    const delay = Math.max(0, Number(candidate.delay_seconds ?? 2)) * 1000;
    const t = setTimeout(() => setShown(candidate), delay);
    return () => clearTimeout(t);
  }, [popups, pathname, user]);

  if (!shown || closed) return null;

  const tone = TONE[shown.tone] || TONE.green;

  const dismiss = () => {
    const map = readDismissed();
    map[shown.id] = Date.now();
    writeDismissed(map);
    setClosed(true);
  };
  // Background click closes the popup for this session only — doesn't persist a
  // 7-day dismissal (only the explicit X button or CTA does that).
  const softClose = () => setClosed(true);

  const cta = shown.cta_link && shown.cta_text ? (
    <Link
      to={shown.cta_link}
      onClick={dismiss}
      data-testid="popup-cta"
      className={`mt-5 inline-block rounded-full px-6 py-2.5 font-bold text-sm ${tone.btn}`}
    >
      {shown.cta_text}
    </Link>
  ) : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 sm:p-6 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="popup-overlay"
      onClick={softClose}
    >
      <div
        className={`relative w-full max-w-md rounded-3xl shadow-2xl p-7 sm:p-8 ${tone.wrap}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="popup-card"
      >
        <button
          type="button"
          onClick={dismiss}
          data-testid="popup-close"
          aria-label="Close popup"
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/15 transition-colors"
        >
          <X size={18} />
        </button>
        <div className={`text-[10px] font-bold uppercase tracking-[0.32em] ${tone.accent}`}>EGGMAN</div>
        <div className="font-display font-black text-2xl sm:text-3xl mt-2 leading-tight" data-testid="popup-title">
          {shown.title}
        </div>
        <p className="mt-3 text-sm leading-relaxed opacity-90 whitespace-pre-line" data-testid="popup-body">
          {shown.body}
        </p>
        {cta}
      </div>
    </div>
  );
}

/**
 * Wrapper that loads /api/cms and feeds the active popups into PopupManager.
 * Mount this near the BrowserRouter so it sees the location.
 */
export function PopupHost() {
  const [popups, setPopups] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/cms").then(({ data }) => {
      if (!alive) return;
      const all = (data?.popups || []).filter((p) => p && p.active !== false);
      setPopups(all);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return <PopupManager popups={popups} />;
}
